import React from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../utils/api";

// Función para formatear fechas de forma segura
const formatSafeDate = (dateString) => {
  if (!dateString) return "Fecha no disponible";

  try {
    let date;
    
    // Si viene en formato MySQL (YYYY-MM-DD HH:mm:ss)
    if (typeof dateString === 'string' && dateString.includes('-')) {
      date = new Date(dateString);
    } 
    // Si viene como timestamp numérico
    else if (!isNaN(dateString)) {
      date = new Date(parseInt(dateString) * 1000);
    }
    // Otros formatos
    else {
      date = new Date(dateString);
    }

    // Verificar que la fecha es válida
    if (isNaN(date.getTime())) {
      //console.warn('Fecha inválida en post:', dateString);
      return "Fecha no disponible";
    }

    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    //console.warn('Error procesando fecha:', dateString, error);
    return "Fecha no disponible";
  }
};

export const PostCard = ({ post, variant = "default", searchQuery }) => {
  const isHorizontal = variant === "horizontal";

  // Función para resaltar texto de búsqueda
  const highlightSearchTerm = (text, query) => {
    if (!query || !text) return text;
    
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} style={{ 
          backgroundColor: '#fef08a', 
          padding: '0 2px',
          borderRadius: '2px'
        }}>
          {part}
        </mark>
      ) : part
    );
  };

  return (
    <article
      className={`post-card ${variant}`}
      style={{
        backgroundColor: "white",
        borderRadius: "12px",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: isHorizontal ? "row" : "column",
        transition: "all 0.3s ease",
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow =
          "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
      }}
    >
      {/* Imagen destacada con enlace */}
      {post.image && (
        <Link
          to={`/post/${post.slug}`}
          className="post-image-link"
          style={{
            width: isHorizontal ? "40%" : "100%",
            height: isHorizontal ? "auto" : "200px",
            minHeight: isHorizontal ? "150px" : "200px",
            position: "relative",
            overflow: "hidden",
            display: "block",
            textDecoration: "none",
          }}
        >
          <div
            className="post-image"
            style={{
              width: "100%",
              height: "100%",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <img
              src={
                post.image?.startsWith("http")
                  ? post.image
                  : `${API_BASE_URL}/uploads/${post.image}`
              }
              alt={post.title}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "transform 0.3s ease",
              }}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "scale(1)";
              }}
            />

            {/* Placeholder si no carga la imagen */}
            <div
              style={{
                display: "none",
                width: "100%",
                height: "100%",
                backgroundColor: "#f3f4f6",
                alignItems: "center",
                justifyContent: "center",
                color: "#9ca3af",
                fontSize: "3rem",
              }}
            >
              📰
            </div>

            {/* Overlay de hover para indicar que es clickeable */}
            <div
              className="image-overlay"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background-color 0.3s ease",
                opacity: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.1)";
                e.currentTarget.style.opacity = "1";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0)";
                e.currentTarget.style.opacity = "0";
              }}
            >
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  padding: "8px 12px",
                  borderRadius: "20px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "0.875rem",
                  fontWeight: "500",
                  color: "#374151",
                  transform: "translateY(10px)",
                  transition: "transform 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <i className="fas fa-eye" style={{ fontSize: "0.8rem" }}></i>
                Leer artículo
              </div>
            </div>

            {/* Badge de categoría si existe */}
            {(post.category_name || post.category_slug) && (
              <div
                style={{
                  position: "absolute",
                  top: "12px",
                  left: "12px",
                  backgroundColor: "rgba(0, 0, 0, 0.8)",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: "6px",
                  fontSize: "0.75rem",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  zIndex: 2,
                }}
              >
                {post.category_name || post.category_slug}
              </div>
            )}

            {/* Badge especial para resultados de búsqueda */}
            {variant === "search" && (
              <div
                style={{
                  position: "absolute",
                  top: "12px",
                  right: "12px",
                  backgroundColor: "rgba(59, 130, 246, 0.9)",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: "6px",
                  fontSize: "0.75rem",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  zIndex: 2,
                }}
              >
                <i className="fas fa-search" style={{ fontSize: "0.7rem" }}></i>
                Resultado
              </div>
            )}
          </div>
        </Link>
      )}

      {/* Contenido del post */}
      <div
        className="post-content"
        style={{
          padding: isHorizontal ? "20px" : "24px",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {/* Título */}
        <div>
          <h3
            style={{
              fontSize: isHorizontal ? "1.1rem" : "1.25rem",
              fontWeight: "bold",
              marginBottom: "12px",
              lineHeight: "1.4",
              color: "#1f2937",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              transition: "color 0.3s ease",
            }}
          >
            <Link
              to={`/post/${post.slug}`}
              style={{
                color: "inherit",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => (e.target.style.color = "#3b82f6")}
              onMouseLeave={(e) => (e.target.style.color = "#1f2937")}
            >
              {searchQuery ? highlightSearchTerm(post.title, searchQuery) : post.title}
            </Link>
          </h3>

          {/* Excerpt/Descripción */}
          {(post.excerpt || post.description) && (
            <p
              style={{
                color: "#6b7280",
                marginBottom: "16px",
                fontSize: "0.9rem",
                lineHeight: "1.5",
                display: "-webkit-box",
                WebkitLineClamp: isHorizontal ? 2 : 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {searchQuery ? 
                highlightSearchTerm(post.excerpt || post.description, searchQuery) : 
                (post.excerpt || post.description)
              }
            </p>
          )}

          {/* Mostrar estadísticas del post si están disponibles */}
          {(post.views_count || post.comments_count) && (
            <div style={{
              display: "flex",
              gap: "16px",
              marginBottom: "12px",
              fontSize: "0.75rem",
              color: "#9ca3af"
            }}>
              {post.views_count && (
                <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <i className="fas fa-eye"></i>
                  {post.views_count} vistas
                </span>
              )}
              {post.comments_count && (
                <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <i className="fas fa-comment"></i>
                  {post.comments_count} comentarios
                </span>
              )}
            </div>
          )}
        </div>

        {/* Footer del post */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "auto",
            paddingTop: "16px",
          }}
        >
          {/* Fecha y autor */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span
              style={{
                fontSize: "0.75rem",
                color: "#9ca3af",
                fontWeight: "500",
              }}
            >
              {formatSafeDate(post.published_at || post.created_at)}
            </span>
            {post.author && (
              <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                Por {post.author}
              </span>
            )}
          </div>

          {/* Botón de leer más */}
          <Link
            to={`/post/${post.slug}`}
            style={{
              backgroundColor: "#3b82f6",
              color: "white",
              padding: "8px 16px",
              borderRadius: "6px",
              fontSize: "0.875rem",
              fontWeight: "500",
              textDecoration: "none",
              transition: "all 0.3s ease",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#2563eb";
              e.target.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#3b82f6";
              e.target.style.transform = "translateY(0)";
            }}
          >
            Leer más
            <i className="fas fa-arrow-right" style={{ fontSize: "0.75rem" }}></i>
          </Link>
        </div>
      </div>
    </article>
  );
};